// An arbitrary participant's camera (or screen share) - the "Speaker" layer. Needs an active call session
// (host.callContext, provided by the studio/guest/render pages, absent on a plain overlay) to actually receive
// video; otherwise it explains why it's empty rather than showing a fake feed.
import { createParticipantOverlay } from '../participantOverlay';
import { watchPicture } from '../../streamPicture';

export default async function mountSpeaker(host) {
	// Seeds host.current with the layer's own settings before anything reads them.
	await host.loadSettings();
	const video = document.createElement('video');
	video.autoplay = true;
	video.muted = true;
	video.playsInline = true;
	// fill, not cover: this layer's box is already the crop/stretch the editor's own layer tools computed
	// (same as an image/video layer's own content) - covering again here would crop it a second time.
	Object.assign(video.style, { width: '100%', height: '100%', objectFit: 'fill', display: 'none' });
	const placeholder = document.createElement('div');
	Object.assign(placeholder.style, {
		width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
		background: 'rgba(255,255,255,.08)', color: '#fff', fontSize: '.8rem', textAlign: 'center', padding: '.5rem',
	});
	host.container.appendChild(video);
	host.container.appendChild(placeholder);
	const overlay = createParticipantOverlay(host.container);

	let receiver = null;
	let stopWatching = null;

	function showStream(stream) {
		video.srcObject = stream;
		video.style.display = 'block';
		placeholder.style.display = 'none';
		overlay.setCameraOff(false);
		overlay.setStream(stream);
	}
	// Joined mic-only: no picture, but they're still there and can still speak.
	function showCameraOff(stream) {
		// Stays displayed (the placeholder covers it): frame watching needs the video to keep playing.
		video.style.display = 'block';
		placeholder.style.display = 'none';
		overlay.setCameraOff(true);
		overlay.setStream(stream);
	}
	function showMessage(text) {
		placeholder.textContent = text;
		placeholder.style.display = 'flex';
		video.style.display = 'none';
		overlay.setCameraOff(false);
		overlay.setStream(null);
	}

	// Named after the caller this layer points at (plus which of their streams, when it isn't the camera).
	let callers = [];
	function refreshName() {
		const { callerId, deviceId } = host.current || {};
		const isSelf = !!callerId && callerId === host.callContext?.ownCallerId?.();
		// The studio's own caller isn't in its list, so it's described from what the studio knows directly.
		const caller = isSelf ? { callerId, name: host.callContext.ownName?.(), micMuted: host.callContext.ownMicMuted?.() } : callers.find(c => c.callerId === callerId);
		if(!caller) { overlay.setName(''); overlay.setMuted(false); return; }
		const name = caller.name || (isSelf ? 'You' : 'Guest');
		overlay.setMuted(!deviceId && !!caller.micMuted);
		if(!deviceId) overlay.setName(name);
		else if(deviceId === caller.screenId) overlay.setName(`${name} (screen)`);
		else overlay.setName(`${name} - ${caller.devices?.find(d => d.id === deviceId)?.label || 'camera'}`);
	}
	const unsubscribeCallers = host.callContext?.onCallers?.(list => { callers = list || []; refreshName(); });
	// Fires when the studio's own mic is toggled, which is when a layer on the studio's own caller has to re-check it.
	const unsubscribeOwnStream = host.callContext?.onOwnStream?.(() => refreshName());

	function connect() {
		receiver?.close();
		receiver = null;
		stopWatching?.();
		stopWatching = null;
		const { callerId, deviceId } = host.current || {};
		if(!callerId) { showMessage('No speaker chosen yet'); return; }
		if(!host.callContext?.openReceiver) { showMessage('Speaker preview needs an active call session'); return; }
		showMessage('Connecting…');
		receiver = host.callContext.openReceiver(callerId, deviceId);
		receiver.onStream(stream => {
			stopWatching?.();
			stopWatching = null;
			if(!stream) { showMessage('Waiting for this speaker…'); return; }
			video.srcObject = stream;
			// A screen share legitimately sends few frames when nothing on it moves, so it's not frame-watched.
			const caller = callers.find(c => c.callerId === callerId);
			const isScreen = !!deviceId && deviceId === caller?.screenId;
			stopWatching = watchPicture(stream, has => has ? showStream(stream) : showCameraOff(stream), { video: isScreen ? null : video });
		});
	}

	connect();
	refreshName();
	host.onSettingsChange(() => { connect(); refreshName(); });
	host.onDestroy(() => {
		unsubscribeCallers?.();
		unsubscribeOwnStream?.();
		stopWatching?.();
		overlay.destroy();
		receiver?.close();
		video.remove();
		placeholder.remove();
	});
}
